import { and, asc, desc, eq, isNotNull, like, sql } from "drizzle-orm";
import { block, page, page_version, type PowerSyncDrizzleDB } from "@folio/powersync";
import {
  DECK_BLOCK_TYPE,
  PDF_ANNOTATIONS_BLOCK_TYPE,
  WORKBOOK_BLOCK_TYPE,
} from "../documents/kinds";
import { livePageFilters } from "../live/filters";

/**
 * SQL predicate: the page owns a document content block of `kind` (a workbook
 * or deck blob). Correlated `EXISTS` over `block` — reads `block`, not `page`,
 * so it does not count as a soft-delete read of `page` (see the live guard).
 */
function hasDocumentBlock(kind: string) {
  return sql`EXISTS (SELECT 1 FROM ${block} AS doc_block WHERE doc_block.page_id = ${page.id} AND doc_block.type = ${kind})`;
}

/** SQL predicate: the page is a plain note (NOT a workbook/deck document). */
function isNotDocumentPage() {
  return sql`NOT EXISTS (SELECT 1 FROM ${block} AS doc_block WHERE doc_block.page_id = ${page.id} AND doc_block.type IN (${WORKBOOK_BLOCK_TYPE}, ${DECK_BLOCK_TYPE}, ${PDF_ANNOTATIONS_BLOCK_TYPE}))`;
}

/** Backlinks panel row: a page that links to the current one. */
export interface BacklinkRow {
  id: string;
  title: string | null;
  icon: string | null;
}

/**
 * All pages visible locally (own + shared with me).
 * PowerSync only syncs accessible rows, so there is no need to filter
 * by user_id: the local database is already the user's scope. Pages in
 * trash (deleted_at) are EXCLUDED from all regular views.
 */
export function getPagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(page)
    .where(and(isNotDocumentPage(), ...livePageFilters()))
    .orderBy(asc(page.sort_key), asc(page.created_at));
}

/**
 * SPREADSHEET library: live pages whose content block is a workbook (blob).
 * Ordered by recent edit. The trash is excluded (livePageFilters); a deleted
 * sheet is restored from /trash like any other page.
 */
export function getWorkbookPagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(page)
    .where(and(hasDocumentBlock(WORKBOOK_BLOCK_TYPE), ...livePageFilters()))
    .orderBy(desc(page.updated_at), asc(page.created_at));
}

/**
 * PRESENTATION library: live pages whose content block is a deck (blob).
 * Same pattern as `getWorkbookPagesQuery`.
 */
export function getDeckPagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(page)
    .where(and(hasDocumentBlock(DECK_BLOCK_TYPE), ...livePageFilters()))
    .orderBy(desc(page.updated_at), asc(page.created_at));
}

/**
 * Detail of a page by id. It does NOT filter the trash: the route decides what
 * to show (a page in the trash is treated as not found).
 */
export function getPageQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db.select().from(page).where(eq(page.id, pageId)).limit(1);
}

/**
 * Blocks of a page, in document order.
 */
export function getPageBlocksQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db
    .select()
    .from(block)
    .where(eq(block.page_id, pageId))
    .orderBy(asc(block.sort_key), asc(block.created_at));
}

/** Lightweight analytics row: only what is needed to count words/links. */
export interface BlockStatRow {
  page_id: string | null;
  text: string | null;
  content: string | null;
  updated_at: string | null;
}

/**
 * All locally visible blocks, in lightweight form for analytics
 * (word count, link map, activity by date). Local-first: runs on SQLite
 * with no round-trip. Excludes pages in the trash.
 */
export function getAllBlocksQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      page_id: block.page_id,
      text: block.text,
      content: block.content,
      updated_at: block.updated_at,
    })
    .from(block)
    .innerJoin(page, eq(block.page_id, page.id))
    .where(and(...livePageFilters()));
}

/**
 * Locally derived backlinks with no extra table: pages whose blocks contain
 * a wikiLink node targeting `pageId`. wikiLink JSON always serializes
 * `"pageId":"<uuid>"` (without spaces), so a LIKE over the content is exact
 * in practice — and it runs on local SQLite for free.
 */
export function getBacklinksQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db
    .selectDistinct({ id: page.id, title: page.title, icon: page.icon })
    .from(block)
    .innerJoin(page, eq(block.page_id, page.id))
    .where(and(like(block.content, `%"pageId":"${pageId}"%`), ...livePageFilters()))
    .orderBy(asc(page.title));
}

/**
 * Basic page search by title.
 */
export function searchPagesQuery(db: PowerSyncDrizzleDB, term: string) {
  return db
    .select()
    .from(page)
    .where(and(like(page.title, `%${term}%`), ...livePageFilters()))
    .orderBy(asc(page.title))
    .limit(50);
}

/**
 * Trash (L2): soft-deleted pages, newest first.
 * FLAT list (includes subpages dragged down by their parent): each row can
 * be restored or permanently deleted separately.
 */
export function getTrashedPagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(page)
    .where(isNotNull(page.deleted_at))
    .orderBy(desc(page.deleted_at), asc(page.title));
}

/**
 * LOCAL version history for a page (PowerSync bucket limited to the
 * sync window). UUIDv7 id ⇒ descending order = newest first.
 * Deeper history is requested on demand through the paginated API.
 */
export function getPageVersionsQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db
    .select()
    .from(page_version)
    .where(eq(page_version.page_id, pageId))
    .orderBy(desc(page_version.id));
}

/**
 * PDF annotation documents are keyed by the PDF's content fingerprint (the
 * file itself never leaves the device): find the live page whose
 * `pdf-annotations` content block carries this fingerprint. The serializer
 * always writes `"fingerprint":"<hex>"` without spaces, so a LIKE over the
 * JSON is exact in practice (same trick as `getBacklinksQuery`) — and it runs
 * on local SQLite, free.
 */
export function getPdfAnnotationPageByFingerprintQuery(
  db: PowerSyncDrizzleDB,
  fingerprint: string,
) {
  return db
    .select({ page_id: block.page_id })
    .from(block)
    .innerJoin(page, eq(block.page_id, page.id))
    .where(
      and(
        eq(block.type, PDF_ANNOTATIONS_BLOCK_TYPE),
        like(block.content, `%"fingerprint":"${fingerprint}"%`),
        ...livePageFilters(),
      ),
    )
    .limit(1);
}
